import React, { useState } from 'react';
import { Plus, Edit, Trash2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { useDataManager } from '../../hooks/useDataManager';

const TestimonialManagement = () => {
  const { testimonials, addTestimonial, updateTestimonial, deleteTestimonial } = useDataManager();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    comment: '',
    product: '',
    verified: true,
    date: new Date().toISOString().split('T')[0]
  });

  const handleAddNew = () => {
    setEditingTestimonial(null);
    setFormData({
      name: '',
      rating: 5,
      comment: '',
      product: '',
      verified: true,
      date: new Date().toISOString().split('T')[0]
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData(testimonial);
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      deleteTestimonial(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      rating: parseInt(formData.rating)
    };
    if (editingTestimonial) {
      updateTestimonial(editingTestimonial.id, data);
    } else {
      addTestimonial(data);
    }
    setIsDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Testimonials</h2>
          <p className="text-gray-600 mt-1">Manage customer reviews</p>
        </div>
        <Button
          onClick={handleAddNew}
          className="bg-[#4a1a1f] hover:bg-[#5a2a2f] text-white"
        >
          <Plus size={18} className="mr-2" />
          Add Testimonial
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id}>
            <CardHeader>
              <div className="flex gap-1 mb-2">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#c9a86a] text-[#c9a86a]" />
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm italic mb-3">"{testimonial.comment}"</p>
              <p className="font-semibold text-sm">{testimonial.name}</p>
              <p className="text-xs text-gray-500">Product: {testimonial.product}</p>
              <p className="text-xs text-gray-400 mt-1">{testimonial.date}</p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(testimonial)}
                >
                  <Edit size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(testimonial.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Customer Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ayesha Khan"
                  required
                />
              </div>
              <div>
                <Label htmlFor="rating">Rating *</Label>
                <Input
                  id="rating"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="comment">Review Comment *</Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Amazing quality at reasonable price..."
                rows={3}
                required
              />
            </div>
            <div>
              <Label htmlFor="product">Product Name *</Label>
              <Input
                id="product"
                value={formData.product}
                onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                placeholder="Elegant Black Abaya"
                required
              />
            </div>
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#4a1a1f] hover:bg-[#5a2a2f]">
                {editingTestimonial ? 'Update' : 'Add'} Testimonial
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialManagement;
